import { Icon, Intent, Spinner, Tag } from '@blueprintjs/core';
import { Classes as PopoverClasses, Tooltip2 } from '@blueprintjs/popover2';
import { SearchResultStatus } from '@retro-ui/shared';
import cx from 'classnames';
import { capitalize } from 'lodash';
import { NavLink } from 'react-router-dom';
import { ButtonCopy } from '@moleculeone/ui';

import { ellipsiseTargetSmiles } from '../util/ellipsiseTargetSmiles';
import { MoleculePreview } from '../../create-batch-search/components/MoleculePreview';

import cls from './SearchResultListPageRow.module.css';

export function SearchResultListPageRow(request, hasReferenceCount: boolean) {
  return [
    {
      label: 'SMILES',
      grow: true,
      renderer: item => (
        <>
          <ButtonCopy
            copy={item.targetSmiles}
            weight="minimal"
            size="small"
            tooltipText="Copy SMILES to clipboard"
            stopPropagation
          />
          <MoleculePreview
            className={cls.targetSmilesPopoverTarget}
            smiles={item.targetSmiles}
            trigger={
              <NavLink
                data-cy="search-result-smiles"
                className={cls.targetSmiles}
                to={`/dashboard/batch/${item.batchId}/result/${item.id}`}
              >
                {ellipsiseTargetSmiles(item.targetSmiles)}
              </NavLink>
            }
          />
        </>
      ),
      headingCellProps: {
        className: cls.headingCell,
      },
      cellProps: {
        className: cls.targetSmilesCell,
      },
    },
    {
      label: 'NAME',
      renderer: item => (
        <span className={item.targetMetadata?.name ? '' : cls.noName} data-cy="search-result-name">
          {item.targetMetadata?.name ?? 'Unnamed'}
        </span>
      ),
      headingCellProps: {
        className: cls.headingCell,
      },
    },
    {
      label: 'STATUS',
      renderer: item => {
        switch (item.status) {
          case SearchResultStatus.COMPLETED:
            return (
              <Icon
                icon="tick-circle"
                intent={Intent.SUCCESS}
                data-cy="search-result-status-completed"
              />
            );
          case SearchResultStatus.FAILED:
            return (
              <Tag round intent={Intent.DANGER} data-cy="search-result-status-failed">
                Error
              </Tag>
            );
          case SearchResultStatus.RUNNING:
            return (
              <div
                style={{ display: 'flex', alignItems: 'flex-start' }}
                data-cy="search-result-status-running"
              >
                <Spinner size={16} intent={Intent.WARNING} value={100} />
              </div>
            );
          default:
            return (
              <Tag round intent={Intent.WARNING}>
                {capitalize(item.status)}
              </Tag>
            );
        }
      },
      headingCellProps: {
        className: cls.headingCell,
      },
    },
    {
      label: () => {
        return (
          <>
            <span className={cls.scoreHead}>SCORE</span>
            <Tooltip2
              placement="top"
              content="Summarizes how hard and expensive the synthesis is. Higher values means harder synthesis."
            >
              <Icon icon="help" className={cls.scoreHelp} />
            </Tooltip2>
          </>
        );
      },
      renderer: item => {
        if (!item.result || item.result < 1) {
          return 'n/a';
        }
        const isNotFound = item.result === 10;
        if (isNotFound) {
          return (
            <Tooltip2
              placement="top"
              className={cx(PopoverClasses.TOOLTIP2_INDICATOR, cls.notFound)}
              content="10 - synthesis was not found."
            >
              <span data-cy="search-result-score">{Number(item.result).toFixed(2)}</span>
            </Tooltip2>
          );
        }
        return <span data-cy="search-result-score">{Number(item.result).toFixed(2)}</span>;
      },
      headingCellProps: {
        className: cls.headingCell,
      },
    },
    {
      label: () => {
        return (
          <>
            <span className={cls.scoreHead}>SM PRICE</span>
            <Tooltip2
              placement="top"
              content={`Approximate price of the starting materials in the ${request.searchParams.presets.synthesis_scale}-scale synthesis.`}
            >
              <Icon icon="help" className={cls.scoreHelp} />
            </Tooltip2>
          </>
        );
      },
      renderer: data => (Number(data.price) > 0 ? `$${Number(data.price).toFixed(2)}` : 'n/a'),
      headingCellProps: {
        className: cls.headingCell,
      },
    },
    {
      label: 'REACTIONS',
      renderer: item => {
        const commerciallyAvailable = item.reactionCount === 0;
        if (commerciallyAvailable) {
          return (
            <Tooltip2
              placement="top"
              className={cx(PopoverClasses.TOOLTIP2_INDICATOR, cls.commerciallyAvailable)}
              content="0 - Target is commercially available."
            >
              <span>{item.reactionCount}</span>
            </Tooltip2>
          );
        }
        return item.reactionCount ?? 'n/a';
      },
      headingCellProps: {
        className: cls.headingCell,
      },
    },
    ...(hasReferenceCount
      ? [
          {
            label: 'REFERENCES',
            renderer: item => item.referenceCount ?? 'n/a',
            headingCellProps: {
              className: cls.headingCell,
            },
          },
        ]
      : []),
  ];
}
